import { sanitizeTypedAnswerWithGivenUnit } from "./answerMatchUtils";
import { cleanGluedDuplicateUnits } from "./answerUnitUtils";

export const normalizeQuestionType = (question) =>
  String(question?.type || question?.question_type || "")
    .toLowerCase()
    .replace(/_/g, "-");

export const isFillQuestionType = (typeOrQuestion) => {
  const type =
    typeof typeOrQuestion === "string"
      ? typeOrQuestion.toLowerCase().replace(/_/g, "-")
      : normalizeQuestionType(typeOrQuestion);
  return type === "multi-fill" || type === "fill-in-the-blank" || type === "table";
};

export const parsePipeJoinedBlanks = (text) => {
  if (text == null || text === "") return [];
  if (Array.isArray(text)) return text.map((v) => (v == null ? "" : String(v)));
  return String(text)
    .split(/\s*\|\s*/)
    .map((s) => s.trim());
};

export const getFillTypeMarksTotal = (question) => {
  const blanks = question?.answer_data?.blank_marks ?? question?.answer?.blank_marks;
  const labelCount = (question?.answer_data?.blank_labels ?? question?.answer?.blank_labels ?? []).length;
  const correctBlanks = (question?.answer_data?.correct_blanks ?? question?.answer?.correct_blanks ?? []).length;
  const blankCount = labelCount || correctBlanks;
  if (Array.isArray(blanks) && blanks.length > 0 && (blankCount === 0 || blanks.length === blankCount)) {
    // Per-blank marks are fractional whenever a fixed total is split across the blanks (4 over 8 table
    // cells = 0.5 each). parseInt truncated every one of those to 0, so the whole question fell back to
    // its raw mark and each blank silently re-read as 1.
    const total = Math.round(blanks.reduce((sum, m) => sum + (Number(m) || 0), 0) * 100) / 100;
    if (total > 0) return total;
  }
  return Math.max(Number(question?.marks) || 0, Number(question?.default_marks) || 0) || 0;
};

export const coerceFillAnswersArray = (submitted, correct) => {
  if (Array.isArray(submitted)) return submitted;
  if (typeof submitted === "string" && submitted.includes("|")) {
    return parsePipeJoinedBlanks(submitted);
  }
  const correctLen = Array.isArray(correct) ? correct.length : 0;
  if (typeof submitted === "string" && correctLen > 1) {
    const commaParts = submitted.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    if (commaParts.length > 1 && commaParts.length <= correctLen) return commaParts;
  }
  return submitted == null || submitted === "" ? [] : [String(submitted)];
};

export const isPerBlankFillDisplay = (question, submitted, correct) => {
  // image-fill is a multi-blank fill too — isFillQuestionType leaves it out because the TAKE page
  // renders it through its own overlay path and the server withholds correct_blanks there for
  // anti-cheat. After submission it has per-box answers like any other fill, and excluding it here
  // fed the raw scalar into the overlay so the boxes never populated. leafMarkVerdict.js makes the
  // same exception for the same reason.
  if (!isFillQuestionType(question) && normalizeQuestionType(question) !== "image-fill") return false;
  const type = normalizeQuestionType(question);
  if (type === "table" && question?.answer_data?.table_layout?.grid?.length > 0) return true;
  const sub = coerceFillAnswersArray(submitted, correct);
  const cor = Array.isArray(correct) ? correct : null;
  return Array.isArray(sub) && Array.isArray(cor) && cor.length > 0;
};

const stripHtml = (html) => String(html ?? "").replace(/<[^>]*>/g, "").trim();

const UNIT_LABEL_AFTER_BLANK =
  /^(?:hours?|hrs?|hr|minutes?|mins?|min|secs?|seconds?|sec|years?|months?|days?|weeks?|grams?|g|kg|kilograms?|millilitres?|milliliters?|ml|litres?|liters?|l|cm|mm|m|metres?|meters?|km|pence|p)$/i;

/** Ordinal / order-slot captions — not the time unit "second(s)". */
const ORDINAL_BLANK_DESCRIPTOR =
  /^(?:first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth)$/i;

const stripAnswerSpaceMarkers = (plain) =>
  plain
    .replace(/[□_…]{1,}/g, " ")
    .replace(/\.{3,}/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const isUnitCaptionBlankLabel = (label) => {
  const plain = stripHtml(label);
  if (!plain) return false;
  // "Second" matches unit regex `seconds?` — it is an order label, not a time unit.
  if (ORDINAL_BLANK_DESCRIPTOR.test(plain) || ORDINAL_BLANK_DESCRIPTOR.test(stripAnswerSpaceMarkers(plain))) {
    return false;
  }
  if (UNIT_LABEL_AFTER_BLANK.test(plain)) return true;
  const stripped = stripAnswerSpaceMarkers(plain);
  return Boolean(stripped) && UNIT_LABEL_AFTER_BLANK.test(stripped);
};

export const inferBlankLabelPosition = (label) => {
  const plain = stripHtml(label);
  if (!plain) return "before";
  // Order descriptors before unit detection so "Second" is not treated as time-unit "second".
  if (
    /\b(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|box|blank|part|answer|value|column|row|coldest|lowest|smallest|least)\b/i.test(
      plain
    ) &&
    !/\b(warmest|hottest|highest|largest|greatest|last)\b/i.test(plain)
  ) {
    return "before";
  }
  if (isUnitCaptionBlankLabel(plain)) return "after";
  // Slot indices ("1","2","(1)","1.") — same as empty-label "Before blank" in the editor.
  if (/^\(?\d+[a-z]?\)?[.)]?$/i.test(plain) || /^\[\d+[a-z]?\]$/i.test(plain)) {
    return "before";
  }
  if (/[□_…]{1,}|\.{3,}/.test(plain) || plain.includes("=")) return "before";
  // Order endpoints: Coldest _____ … _____ Warmest
  if (/\b(warmest|hottest|highest|largest|greatest|last)\b/i.test(plain)) {
    return "after";
  }
  return "after";
};

// A one-letter label the stem uses as a VARIABLE ("two different positive numbers 𝑛 and 𝑚") is the
// name of the box, not a unit — "m" is not metres there (St Paul's Girls 2025 P1 Sec C Q5b/c).
const MATH_ITALIC_BASE = { lower: 0x1d44e, upper: 0x1d434 };
export const labelIsStemVariable = (label, questionText) => {
  const l = stripHtml(label).trim();
  if (!/^[a-z]$/i.test(l) || !questionText) return false;
  const stem = stripHtml(String(questionText)).replace(/&nbsp;/gi, " ").replace(/\u00a0/g, " ");
  const lower = l === l.toLowerCase();
  const italic = String.fromCodePoint((lower ? MATH_ITALIC_BASE.lower : MATH_ITALIC_BASE.upper) + (l.toLowerCase().charCodeAt(0) - 97));
  if (stem.includes(italic)) return true;
  if (new RegExp(`\\$\\s*${l}\\s*\\$`).test(stem)) return true;
  return new RegExp(`\\b(?:numbers?|values?|integers?|variables?|letters?)\\b[^.?!]*\\b${l}\\b`, "i").test(stem);
};

export const resolveBlankLabelPosition = (label, positions, idx, questionText = "") => {
  if (isUnitCaptionBlankLabel(label) && !labelIsStemVariable(label, questionText)) return "after";
  const plain = stripHtml(label);
  if (ORDINAL_BLANK_DESCRIPTOR.test(plain)) return "before";
  const stored = positions?.[idx];
  if (stored === "after" || stored === "before") return stored;
  return inferBlankLabelPosition(label);
};

export const isFixedFillMarks = (question) => {
  const ad = question?.answer_data || {};
  const ans = question?.answer || {};
  return (ad.marks_mode || ans.marks_mode) === "fixed";
};

export const getFixedFillMarksTotal = (question) => {
  const ad = question?.answer_data || {};
  const ans = question?.answer || {};
  const total = ad.marks_total ?? ans.marks_total ?? question?.marks ?? question?.default_marks;
  return Math.max(0, Number(total) || 0);
};

export const getDisplayFillMarksTotal = (question) => {
  if (isFixedFillMarks(question)) {
    const fixed = getFixedFillMarksTotal(question);
    if (fixed > 0) return fixed;
  }
  return getFillTypeMarksTotal(question);
};

export const getFixedMarksPerBlankLabel = (totalMarks, blankCount) => {
  const n = Math.max(1, blankCount || 1);
  const t = Math.max(0, Number(totalMarks) || 0) || 1;
  return `${Math.round(t * 100) / 100}/${n}`;
};

// A fixed-total fill question splits its mark across the blanks (4 marks over 8 table cells = 0.5 each).
// The split is materialised into blank_marks so every surface that sums per-blank marks agrees with the
// declared total instead of counting 1 per blank; the rounding drift lands on the last blank so the sum
// is exact (5 over 3 → 1.67 / 1.67 / 1.66).
export const distributeFixedBlankMarks = (totalMarks, blankCount) => {
  const n = Math.max(0, Math.trunc(blankCount) || 0);
  const total = Math.max(0, Number(totalMarks) || 0);
  if (n === 0 || total === 0) return [];
  const each = Math.round((total / n) * 100) / 100;
  const arr = Array.from({ length: n }, () => each);
  const drift = Math.round((total - each * n) * 100) / 100;
  if (drift !== 0) arr[n - 1] = Math.round((each + drift) * 100) / 100;
  return arr;
};

/** Per-blank unit chip meta from answer_data.blank_units[i] (server-inferred, non-secret). */
export const getBlankUnitMeta = (question, idx) => {
  const raw = question?.answer_data?.blank_units?.[idx];
  if (raw && typeof raw === "object") {
    const unit = String(raw.unit ?? raw.suffix ?? "").trim();
    if (unit) return { unit, prefix: raw.prefix === true };
  }
  const boxes = question?.answer_data?.overlay_layout?.blanks;
  const box = Array.isArray(boxes) ? boxes[idx] : null;
  if (box && typeof box === "object") {
    const unit = String(box.unit ?? box.suffix ?? "").trim();
    if (unit) return { unit, prefix: box.prefix === true || box.unit_prefix === true };
  }
  const html = `${question?.question_text || ""} ${JSON.stringify(question?.answer_data?.table_layout || {})}`;
  // A real area unit only: lower-case, not the tail of a word, and a plain "2" needs a number before it.
  // Case-blind, "transformation M1 followed by M2" read as m² and every corner box wore an "m²" chip
  // (MGS Arithmetic 2 2013 Q9b).
  const areaHits = [...html.matchAll(/(?:^|[^A-Za-z])((?:cm|mm|km|m)\s*²|\d\s*(?:cm|mm|km|m)\s*\^?2(?!\d))/g)]
    .map((hit) => hit[1].replace(/^\d\s*/, "").replace(/\^/, ""));
  const canon = [...new Set(areaHits.map((u) => u.replace(/\s+/g, "").replace(/2$/i, "²").toLowerCase()))];
  if (canon.length === 1 && /cm²$/i.test(canon[0])) return { unit: "cm²", prefix: false };
  if (canon.length === 1 && /mm²$/i.test(canon[0])) return { unit: "mm²", prefix: false };
  if (canon.length === 1 && /m²$/i.test(canon[0])) return { unit: "m²", prefix: false };
  return { unit: null, prefix: false };
};

const CURRENCY_PREFIX_UNITS = new Set(["£", "$", "€", "¥"]);

/**
 * Two or more columns of overlay blanks that would all wear the same currency
 * prefix. The figure's own column headings (£ and p) already name the units.
 * Mirrors repeatedCurrencyPrefixOnOverlayColumns in the mobile answer-unit helper.
 */
export const repeatedCurrencyPrefixOnOverlayColumns = (question, units) => {
  const boxes = question?.answer_data?.overlay_layout?.blanks;
  if (!Array.isArray(boxes) || boxes.length < 4) return false;
  const placed = [];
  boxes.forEach((box, index) => {
    const x = Number(box?.x);
    if (!Number.isFinite(x)) return;
    placed.push({ index, x });
  });
  if (placed.length < 4) return false;
  placed.sort((a, b) => a.x - b.x);
  const columns = [];
  let column = [];
  let anchor = placed[0].x;
  for (const point of placed) {
    if (column.length > 0 && point.x - anchor > 0.08) {
      columns.push(column);
      column = [point.index];
      anchor = point.x;
    } else {
      column.push(point.index);
    }
  }
  if (column.length > 0) columns.push(column);
  if (columns.length < 2 || columns.some((entry) => entry.length < 2)) return false;
  const currencies = new Set();
  for (const point of placed) {
    const unit = units?.[point.index];
    if (!unit?.prefix || !CURRENCY_PREFIX_UNITS.has(unit.unit)) return false;
    currencies.add(unit.unit);
  }
  return currencies.size === 1;
};

/** Per-box overlay chips, without a currency prefix repeated on every cell of a multi-column diagram. */
export const overlayUnitsByIndex = (question, count) => {
  const n = Math.max(0, Math.trunc(count) || 0);
  const units = Array.from({ length: n }, (_, i) => getBlankUnitMeta(question, i));
  if (!repeatedCurrencyPrefixOnOverlayColumns(question, units)) return units;
  return units.map(() => ({ unit: null, prefix: false }));
};

// "8 minutes 34 seconds" — one blank whose answer wears two units, so it gets one numeric box per unit
// rather than a single box with one chip (which getBlankUnitMeta cannot produce for a compound).
// Returns null unless the server sent at least two units for this blank.
export const getBlankCompoundUnits = (question, idx) => {
  const raw = question?.answer_data?.blank_compound_units?.[idx];
  return Array.isArray(raw) && raw.length > 1 ? raw.map(String) : null;
};

// One blank can hold a LIST of values ("What are the TWO possible pairs…" → "66, 66"). The unit belongs
// to each of them, so it is joined per value. A comma with exactly three digits after it is a thousands
// separator ("1,200"), not a list.
const BLANK_LIST_SEPARATOR = /,(?!\d{3}(?!\d))/;

const stripOneBlankUnit = (text, unitMeta) => {
  let base = text;
  if (unitMeta.prefix && text.startsWith(unitMeta.unit)) {
    base = text.slice(unitMeta.unit.length);
    base = base.startsWith(" ") ? base.slice(1) : base;
  } else if (!unitMeta.prefix && text.endsWith(unitMeta.unit)) {
    base = text.slice(0, -unitMeta.unit.length);
    base = base.endsWith(" ") ? base.slice(0, -1) : base;
  }
  return sanitizeTypedAnswerWithGivenUnit(base, unitMeta.unit, { isPrefix: !!unitMeta.prefix });
};

const joinOneBlankUnit = (text, unitMeta) => {
  const cleaned = sanitizeTypedAnswerWithGivenUnit(text, unitMeta.unit, { isPrefix: !!unitMeta.prefix });
  if (!cleaned) return "";
  const joined = unitMeta.prefix ? `${unitMeta.unit} ${cleaned}` : `${cleaned} ${unitMeta.unit}`;
  return cleanGluedDuplicateUnits(joined);
};

/** Strip adornment unit for display (mirrors TypeDrawInput). */
export const stripBlankUnitDisplay = (value, unitMeta) => {
  const text = value == null ? "" : String(value);
  if (!unitMeta?.unit) return text;
  const parts = text.split(BLANK_LIST_SEPARATOR);
  if (parts.length < 2) return stripOneBlankUnit(text, unitMeta);
  return parts.map((p) => stripOneBlankUnit(p.trim(), unitMeta)).join(", ");
};

/** Join typed value with adornment unit for storage / grading (mirrors TypeDrawInput). */
export const joinBlankUnitValue = (text, unitMeta) => {
  if (!unitMeta?.unit) return text ?? "";
  if (!text) return "";
  const parts = String(text).split(BLANK_LIST_SEPARATOR);
  if (parts.length < 2) return joinOneBlankUnit(text, unitMeta);
  // An empty part is a comma the student has just typed and not filled in yet ("66, ") — it must
  // survive the round-trip through stripBlankUnitDisplay, or the comma vanishes as it is typed and a
  // second value can never be entered.
  return parts
    .map((p) => p.trim())
    .map((p) => (p === "" ? "" : joinOneBlankUnit(p, unitMeta)))
    .join(", ");
};
